import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { imgUrl } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Send, MessageCircle, Loader2, Lock, LogIn } from "lucide-react";

export default function Chats() {
  const { id } = useParams();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    if (id) {
      setLoading(true);
      api.get(`/messages/thread/${id}`).then((r) => setData(r.data)).catch(() => navigate("/chats")).finally(() => setLoading(false));
    } else {
      setLoading(true);
      api.get("/messages/threads").then((r) => setThreads(r.data)).finally(() => setLoading(false));
    }
  }, [id, user]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setText("");
    try {
      const r = await api.post(`/messages/thread/${id}`, { text: t });
      setData((d) => ({ ...d, messages: [...d.messages, r.data.message, ...(r.data.reply ? [r.data.reply] : [])] }));
    } catch (e) {
      if (e?.response?.status === 402) setLocked(true);
    }
  };

  if (!user) {
    return (
      <div className="min-h-[70vh] bg-[#fff7f0] flex items-center justify-center px-6 text-center">
        <div>
          <MessageCircle className="h-12 w-12 text-[#ff6b5b] mx-auto" />
          <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Sign in to see your chats</h1>
          <Button onClick={login} className="mt-5 rounded-full bg-[#ff6b5b] hover:bg-[#f2523f] text-white"><LogIn className="h-4 w-4 mr-2" />Sign in</Button>
        </div>
      </div>
    );
  }

  if (loading) return <div className="min-h-[60vh] bg-[#fff7f0] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#ff6b5b]" /></div>;

  // Thread view
  if (id && data) {
    const th = data.thread;
    return (
      <div className="min-h-[88vh] bg-[#fff7f0] flex flex-col" data-testid="chat-thread">
        <div className="bg-white border-b border-[#ffe3db] px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
          <button data-testid="chat-back" onClick={() => navigate("/chats")}><ArrowLeft className="h-5 w-5 text-slate-600" /></button>
          <img src={imgUrl(th.cover)} alt="" className="h-10 w-10 rounded-xl object-cover" />
          <div className="min-w-0"><p className="font-semibold text-slate-900 truncate">{th.property_title}</p><p className="text-xs text-slate-400">with {th.owner_name}</p></div>
        </div>
        <div className="flex-1 max-w-lg w-full mx-auto px-4 py-5 space-y-3">
          {data.messages.map((m) => {
            const mine = m.sender_id === user.user_id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[78%] px-4 py-2.5 rounded-2xl text-sm ${mine ? "bg-[#ff6b5b] text-white rounded-br-md" : "bg-white border border-[#ffe3db] text-slate-700 rounded-bl-md"}`}>{m.text}</div>
              </div>
            );
          })}
          {data.messages.length === 0 && <p className="text-center text-slate-400 text-sm mt-10">Say hi 👋 — break the ice!</p>}
          <div ref={endRef} />
        </div>
        {locked ? (
          <div className="bg-white border-t border-[#ffe3db] p-4 text-center">
            <p className="text-sm text-slate-600 flex items-center justify-center gap-1.5"><Lock className="h-4 w-4 text-[#ff6b5b]" /> Your free month ended.</p>
            <Button data-testid="chat-upgrade" onClick={() => navigate("/premium")} className="mt-2 rounded-full bg-[#ff6b5b] hover:bg-[#f2523f] text-white">Unlock chat — ₹199/mo</Button>
          </div>
        ) : (
          <div className="bg-white border-t border-[#ffe3db] p-3 sticky bottom-0">
            <div className="max-w-lg mx-auto flex gap-2">
              <Input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…" className="rounded-full border-[#ffcabf]" />
              <Button data-testid="chat-send" onClick={send} className="rounded-full bg-[#ff6b5b] hover:bg-[#f2523f] text-white h-10 w-10 p-0"><Send className="h-4 w-4" /></Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Inbox list
  return (
    <div className="min-h-[80vh] bg-[#fff7f0]" data-testid="chat-inbox">
      <div className="max-w-lg mx-auto px-5 py-8">
        <h1 className="font-display text-3xl font-extrabold text-slate-900">Your chats 💬</h1>
        <p className="text-slate-500 mt-1">Homes you liked and reached out to.</p>
        <div className="mt-6 space-y-3">
          {threads.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-[#ffe3db]">
              <p className="text-slate-500">No chats yet. Go swipe some homes!</p>
              <Button onClick={() => navigate("/discover")} className="mt-4 rounded-full bg-[#ff6b5b] hover:bg-[#f2523f] text-white">Start swiping</Button>
            </div>
          ) : threads.map((t) => (
            <button key={t.id} data-testid={`thread-${t.id}`} onClick={() => navigate(`/chats/${t.id}`)} className="w-full flex items-center gap-3 bg-white border border-[#ffe3db] rounded-2xl p-3 text-left hover:shadow-md transition-shadow">
              <img src={imgUrl(t.cover)} alt="" className="h-14 w-14 rounded-xl object-cover" />
              <div className="min-w-0 flex-1"><p className="font-semibold text-slate-900 truncate">{t.property_title}</p><p className="text-sm text-slate-400 truncate">{t.last_message || "Say hi 👋"}</p></div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
